import { Alert, Card, Col, Row, Space, Statistic, Tag, Tooltip, Typography } from 'antd';

import useLocale from '../../../hooks/useLocale';
import { ClientService } from '../../../shared/api/ClientService';
import InfoLabel from './InfoLabel';
import LoadErrorAlert from './LoadErrorAlert';
import {
  CATCHING_UP_TAG_COLOR,
  COMMAND_OLDEST_DUE_WARNING_SECONDS,
  COMMAND_STATUS_PRESENTATION,
  DANGER_VALUE_COLOR,
  EMPTY_VALUE,
  FLAG_OFF_COLOR,
  FLAG_ON_COLOR,
  WARNING_VALUE_COLOR,
  WATERMARK_AGE_WARNING_SECONDS,
} from './constants';
import type { CommandStatus } from './types';
import {
  formatAge,
  formatDateTime,
  formatText,
  getStatusLabel,
  hasLeftoverCommands,
  isOldestDueAtRisk,
  isWatermarkLate,
} from './utils';

const Status = ClientService.InsolvCommandStatus;

interface IProps {
  summary?: ClientService.IntegrationSummaryDto;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
}

interface ISummaryCard {
  key: string;
  title: string;
  hint: React.ReactNode;
  value: number | string;
  color?: string;
  isTime?: boolean;
}

interface IFlagTagProps {
  label: string;
  enabled?: boolean;
}

interface ICardRowProps {
  cards: ISummaryCard[];
  isLoading?: boolean;
}

interface IGroupTitleProps {
  title: string;
  children?: React.ReactNode;
}

const FlagTag = ({ label, enabled }: IFlagTagProps): JSX.Element => {
  const { t } = useLocale();

  return (
    <Tag color={enabled ? FLAG_ON_COLOR : FLAG_OFF_COLOR}>
      {formatText(t.INTEGRATION_FLAG_STATE, label, enabled ? t.INTEGRATION_FLAG_ON : t.INTEGRATION_FLAG_OFF)}
    </Tag>
  );
};

const GroupTitle = ({ title, children }: IGroupTitleProps): JSX.Element => (
  <div className="integration-monitoring__group-title">
    <Typography.Text strong>{title}</Typography.Text>
    {children}
  </div>
);

const CardRow = ({ cards, isLoading }: ICardRowProps): JSX.Element => (
  <Row gutter={[16, 16]}>
    {cards.map((card) => (
      <Col key={card.key} xs={24} sm={12} md={8} xl={4}>
        <Card size="small">
          <Statistic
            className={card.isTime ? 'integration-monitoring__stat--time' : undefined}
            title={<InfoLabel label={card.title} hint={card.hint} />}
            value={card.value}
            valueStyle={card.color ? { color: card.color } : undefined}
            loading={isLoading}
          />
        </Card>
      </Col>
    ))}
  </Row>
);

const SummaryPanel = ({ summary, isLoading, isError, error, onRetry }: IProps): JSX.Element => {
  const { t } = useLocale();
  const commands = summary?.commands;
  const events = summary?.events;
  const changeFeed = summary?.changeFeed;
  const heldFileCount = commands?.heldFileCount;
  const oldestDueAgeSeconds = commands?.oldestDueAgeSeconds;
  const isOldestAtRisk = isOldestDueAtRisk(oldestDueAgeSeconds);
  const isWatermarkAgeLate = isWatermarkLate(changeFeed?.watermarkAgeSeconds);

  const statusCard = (status: CommandStatus, count?: number, isAlarming = false): ISummaryCard => ({
    key: String(status),
    title: getStatusLabel(status, t),
    hint: t[COMMAND_STATUS_PRESENTATION[status].meaningKey],
    value: count ?? EMPTY_VALUE,
    color: isAlarming && count ? DANGER_VALUE_COLOR : undefined,
  });

  const commandCards: ISummaryCard[] = [
    statusCard(Status.Pending, commands?.pendingCount),
    statusCard(Status.Retrying, commands?.retryingCount),
    statusCard(Status.NeedsAttention, commands?.needsAttentionCount, true),
    statusCard(Status.DeadLettered, commands?.deadLetteredCount, true),
    {
      key: 'heldFiles',
      title: t.INTEGRATION_HELD_FILES,
      hint: t.INTEGRATION_HELD_FILES_HINT,
      value: heldFileCount ?? EMPTY_VALUE,
      color: heldFileCount ? WARNING_VALUE_COLOR : undefined,
    },
    {
      key: 'oldestWaiting',
      title: t.INTEGRATION_OLDEST_WAITING,
      hint: (
        <>
          <div>{t.INTEGRATION_OLDEST_WAITING_HINT}</div>
          {isOldestAtRisk && (
            <div>
              {formatText(t.INTEGRATION_OLDEST_WAITING_SLA_RISK, formatAge(COMMAND_OLDEST_DUE_WARNING_SECONDS))}
            </div>
          )}
        </>
      ),
      value: formatAge(oldestDueAgeSeconds),
      color: isOldestAtRisk ? DANGER_VALUE_COLOR : undefined,
    },
  ];

  const eventCards: ISummaryCard[] = [
    {
      key: 'eventsProcessed',
      title: t.INTEGRATION_EVENTS_PROCESSED,
      hint: t.INTEGRATION_EVENTS_PROCESSED_HINT,
      value: events?.processedCount ?? EMPTY_VALUE,
    },
    {
      key: 'eventsIgnored',
      title: t.INTEGRATION_EVENTS_IGNORED,
      hint: t.INTEGRATION_EVENTS_IGNORED_HINT,
      value: events?.ignoredCount ?? EMPTY_VALUE,
    },
    {
      // Not limited to the last 24 hours, unlike the two counts before it.
      key: 'eventsLast',
      title: t.INTEGRATION_EVENTS_LAST,
      hint: t.INTEGRATION_EVENTS_LAST_HINT,
      value: formatDateTime(events?.lastEventTime),
      isTime: true,
    },
  ];

  const changeFeedCards: ISummaryCard[] = [
    {
      key: 'watermark',
      title: t.INTEGRATION_WATERMARK,
      hint: t.INTEGRATION_WATERMARK_HINT,
      value: formatDateTime(changeFeed?.watermarkTime),
      isTime: true,
    },
    {
      key: 'watermarkAge',
      title: t.INTEGRATION_WATERMARK_AGE,
      hint: (
        <>
          <div>{t.INTEGRATION_WATERMARK_AGE_HINT}</div>
          {isWatermarkAgeLate && (
            <div>{formatText(t.INTEGRATION_WATERMARK_AGE_LATE, formatAge(WATERMARK_AGE_WARNING_SECONDS))}</div>
          )}
        </>
      ),
      value: formatAge(changeFeed?.watermarkAgeSeconds),
      color: isWatermarkAgeLate ? DANGER_VALUE_COLOR : undefined,
    },
    {
      key: 'lastRun',
      title: t.INTEGRATION_LAST_RUN,
      hint: t.INTEGRATION_LAST_RUN_HINT,
      value: formatDateTime(changeFeed?.lastRunTime),
      isTime: true,
    },
  ];

  return (
    <div className="integration-monitoring__summary">
      {isError && <LoadErrorAlert error={error} onRetry={onRetry} />}

      {summary && (
        <Space className="integration-monitoring__flags" wrap>
          <FlagTag label={t.INTEGRATION_INSOLV_FLAG} enabled={summary.insolvIntegrationEnabled} />
          <FlagTag label={t.INTEGRATION_RPA_FLAG} enabled={summary.rpaIntegrationEnabled} />
        </Space>
      )}

      {hasLeftoverCommands(summary) && (
        <Alert
          className="integration-monitoring__alert"
          type="warning"
          showIcon
          message={t.INTEGRATION_INSOLV_OFF_WARNING}
        />
      )}

      {(summary || isLoading) && (
        <>
          <GroupTitle title={t.INTEGRATION_TAB_COMMANDS} />
          <CardRow cards={commandCards} isLoading={isLoading} />
        </>
      )}

      {events && (
        <>
          <GroupTitle title={t.INTEGRATION_TAB_EVENTS} />
          <CardRow cards={eventCards} />
        </>
      )}

      {changeFeed && (
        <>
          <GroupTitle title={t.INTEGRATION_CHANGE_FEED}>
            {!changeFeed.watermarkTime && (
              <Tooltip title={t.INTEGRATION_NO_RUN_YET_HINT}>
                <Tag>{t.INTEGRATION_NO_RUN_YET}</Tag>
              </Tooltip>
            )}
            {changeFeed.hasCheckpoint && (
              <Tooltip title={t.INTEGRATION_CATCHING_UP_HINT}>
                <Tag color={CATCHING_UP_TAG_COLOR}>{t.INTEGRATION_CATCHING_UP}</Tag>
              </Tooltip>
            )}
          </GroupTitle>
          <CardRow cards={changeFeedCards} />
        </>
      )}
    </div>
  );
};

export default SummaryPanel;
